import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../layout/SubHeader/SubHeader';
import Page from '../../layout/Page/Page';
import showNotification from '../../components/extras/showNotification';
import Icon from '../../components/icon/Icon';
import Card, {
	CardActions,
	CardBody,
	CardFooter,
	CardHeader,
	CardLabel,
	CardSubTitle,
	CardTitle,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../components/bootstrap/Dropdown';
import useDarkMode from '../../hooks/useDarkMode';
import Spinner from '../../components/bootstrap/Spinner';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Breadcrumb from '../../components/bootstrap/Breadcrumb';
import Avatar from '../../components/Avatar';
import CommonDesc from '../../common/other/CommonDesc';
import Label from '../../components/bootstrap/forms/Label';
import UserImageWebp from '../../assets/img/wanna/wanna4.webp';
import { userAtom } from '../../_atoms/user';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import { updateAvatar, updatePassword, updateUser } from '../../_services/user';
import Swal from 'sweetalert2';
import Textarea from '../../components/bootstrap/forms/Textarea';

const User = () => {
	const [user, setUser] = useAtom(userAtom)
	const { themeStatus } = useDarkMode();
	const [files, setFiles] = useState([])
	const inputRef = useRef()
	const { darkModeStatus } = useDarkMode()
	const { token } = user
	/**
	 * Common
	 */
	const [lastSave, setLastSave] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [tag, setTag] = useState("")
	const [tags, setTags] = useState([])
	const [description, setDescription] = useState("")
	const [instagram, setInstagram] = useState("")
	const [passwordChangeCTA, setPasswordChangeCTA] = useState(false);


	const handleSave = () => {
		const data = { id: user._id, description, tags, instagram }
		setIsLoading(true)
		updateUser(token, data)
			.then(res => {
				setIsLoading(false)
				if (res.data && res.data.code === 200) {
					showNotification(
						<span className='d-flex align-items-center'>
							<Icon icon='Info' size='lg' className='me-1' />
							<span>Başarılı</span>
						</span>,
						"Bilgiler güncellendi",
					);
				} else {
					showNotification(
						<span className='d-flex align-items-center'>
							<Icon icon='Info' size='lg' className='me-1' />
							<span>Hata</span>
						</span>,
						"Girdiğiniz bilgiler güncellenemedi",
					);
				}
			})
			.catch(err => {
				setIsLoading(false)
				showNotification(
					<span className='d-flex align-items-center'>
						<Icon icon='Info' size='lg' className='me-1' />
						<span>Hata</span>
					</span>,
					"Bir hata oluştu.",
				);
			})
	};

	useEffect(() => {
		if (user && user._id) {
			setTags(user.tags || [])
			setDescription(user.description || "")
			setInstagram(user.instagram || "")
		}
	}, [user])

	const formik = useFormik({
		initialValues: {
			firstname: user.firstname || "",
			familyname: user.familyname || "",
			email: user.email || "",
			phone: user.phone || "",
			landphone: user.landphone || "",
			tags: user.tags || [],
			password: '',
			newPassword: '',
			confirmPassword: '',
			checkOne: true,
			checkTwo: false,
			checkThree: true,
		},
		onSubmit: (values) => {
			const data = { password: values.password, newPassword: values.newPassword, id: user._id }
			updatePassword(token, data)
				.then(res => {
					setIsLoading(false)
					if (res.data && res.data.code === 200) {
						return Swal.fire({
							icon: 'success',
							title: 'Şifre güncellendi!',
							timer: 2000,
							showConfirmButton: false
						})
						setPasswordChangeCTA(false)
					} else {
						return Swal.fire({
							icon: 'error',
							title: 'Bir hata oluştu.'
						})
					}
				})
				.catch(error => {
					setIsLoading(false)
					return Swal.fire({
						icon: 'error',
						title: 'Bir hata oluştu : ' + error
					})
				})
		},
	});

	const onChange = (e) => {
		const filesUploaded = e.target.files;
		setFiles(filesUploaded)
	};


	const submitOperation = fd => {
		setIsLoading(true)
		updateAvatar(token, fd)
			.then(res => {
				setIsLoading(false)
				if (res.data && res.data.code === 200) {
					setUser({ ...user, avatar: res.data.data })
					return Swal.fire({
						icon: 'success',
						title: 'Avatar güncellendi!',
						timer: 2000,
						showConfirmButton: false
					})
				} else {
					return Swal.fire({
						icon: 'error',
						title: 'Bir hata oluştu.'
					})
				}
			})
			.catch(error => {
				setIsLoading(false)
				return Swal.fire({
					icon: 'error',
					title: 'Bir hata oluştu : ' + error
				})
			})
	}

	const handleSubmit = () => {
		if (files.length === 0) {
			Swal.fire({
				icon: 'error',
				title: 'Lütfen bir dosya seçiniz.'
			})
		} else {
			const fd = new FormData();
			fd.append('id', user._id)
			for (let i = 0; i < files.length; i++) {
				fd.append(('avatar'), files[i], files[i].name)
			}
			submitOperation(fd)
			setFiles([])
		}
	}

	useEffect(() => {
		if (files && files.length > 0) {
			handleSubmit()
		}
	}, [files])


	return (
		<PageWrapper title="Profil">
			<SubHeader>
				<SubHeaderLeft>
					<Breadcrumb
						list={[
							{ title: 'Anasayfa', to: '/' },
							{ title: 'Profil', to: '/profil' },
						]}
					/>
					<SubheaderSeparator />
					<span className='text-muted'>{user.firstname} {user.familyname}</span>
				</SubHeaderLeft>
				<SubHeaderRight>
					<Button
						icon={isLoading ? undefined : 'Save'}
						isLight
						color={lastSave ? 'info' : 'success'}
						isDisable={isLoading}
						onClick={handleSave}>
						{isLoading && <Spinner isSmall inButton />}
						{isLoading
							? (lastSave && 'Saving') || 'Kaydediliyor'
							: (lastSave && 'Save') || 'Kaydet'}
					</Button>
				</SubHeaderRight>
			</SubHeader>
			<Page>
				<div className='row h-100 align-content-start'>
					<div className='col-md-8'>
						<Card>
							<CardBody>
								<div className='col-12'>
									<div className='row g-4 align-items-center'>
										<div className='col-lg-auto'>
											<Avatar
												srcSet={user.avatar ? user.avatar : UserImageWebp}
												src={user.avatar ? user.avatar : UserImageWebp}
												color="#ffa500"
												rounded={3}
											/>
										</div>
										<div className='col-lg'>
											<div className='row g-4'>
												<div className='col-auto'>
													<Button icon='Upload' color='info' isLight onClick={() => inputRef.current?.click()}>Avatar Yükle</Button>
													<input
														type="file"
														ref={inputRef}
														style={{ display: "none" }}
														accept="image/jpeg,image/webp,image/jpg,image/png, image/gif"
														onChange={onChange}
													/>
												</div>
												<div className='col-auto'>
													<Button color='dark' isLight icon='Delete'>
														Avatarı Sil
													</Button>
												</div>
												<div className='col-12'>
													<p className='lead text-muted'>
														Avatar diğer kullanıcıların sizi tanımasına yardımcı olur.
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
						<Card>
							<CardHeader>
								<CardLabel icon='Person' iconColor='success'>
									<CardTitle tag='div' className='h5'>
										Kişisel Bilgiler
									</CardTitle>
									<CardSubTitle tag='div' className='h6'>
										Kullanıcı Bilgileri
									</CardSubTitle>
								</CardLabel>
							</CardHeader>
							<CardBody>
								<div className='row g-4'>
									<div className='col-md-6'>
										<FormGroup id='firstname' label='Adınız' isFloating>
											<Input
												placeholder='Adınız'
												autoComplete='additional-name'
												onBlur={formik.handleBlur}
												value={formik.values.firstname}
												isValid={formik.isValid}
												isTouched={formik.touched.firstname}
												invalidFeedback={formik.errors.firstname}
												validFeedback='Geçerli!'
											/>
										</FormGroup>
									</div>
									<div className='col-md-6'>
										<FormGroup id='lastname' label='Soyadınız' isFloating>
											<Input
												placeholder='Soyadınız'
												autoComplete='family-name'
												onBlur={formik.handleBlur}
												value={formik.values.familyname}
												isValid={formik.isValid}
												isTouched={formik.touched.familyname}
												invalidFeedback={formik.errors.familyname}
												validFeedback='Geçerli!'
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<FormGroup
											id='email'
											label='Eposta'
											isFloating
											isDisable
											formText='Adınız, soyadınız ve eposta adresiniz yöneticiniz tarafından sisteme tanımlanır ve değiştirilemez'>
											<Input
												placeholder='Eposta'
												autoComplete='email'
												onChange={() => null}
												onBlur={formik.handleBlur}
												isDisable
												value={formik.values.email}
												isValid={formik.isValid}
												isTouched={formik.touched.email}
												invalidFeedback={formik.errors.email}
											/>
										</FormGroup>
									</div>
								</div>
							</CardBody>
						</Card>
						<Card>
							<CardHeader>
								<CardLabel icon='Phonelink' iconColor='danger'>
									<CardTitle tag='div' className='h5'>
										İletişim Bilgileri
									</CardTitle>
									<CardSubTitle tag='div' className='h6'>
										Kullanıcı iletişim bilgileri
									</CardSubTitle>
								</CardLabel>
							</CardHeader>
							<CardBody>
								<div className='row g-4'>
									<div className='col-md-6'>
										<FormGroup id='phone' label='Telefon' isFloating>
											<Input
												type='tel'
												placeholder='Telefon'
												autoComplete='tel'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.phone}
												isValid={formik.isValid}
												isTouched={formik.touched.phone}
												invalidFeedback={formik.errors.phone}
												validFeedback='Geçerli!'
											/>
										</FormGroup>
									</div>
									<div className='col-md-6'>
										<FormGroup
											id='landphone'
											label='Sabit Telefon / Dahili'
											isFloating>
											<Input
												type='tel'
												placeholder='Sabit Telefon / Dahili'
												autoComplete='tel'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.landphone}
												isValid={formik.isValid}
												isTouched={formik.touched.landphone}
												invalidFeedback={formik.errors.landphone}
												validFeedback='Geçerli!'
											/>
										</FormGroup>
									</div>
								</div>
							</CardBody>
						</Card>
						<Card>
							<CardHeader>
								<CardLabel icon='LocalPolice' iconColor='primary'>
									<CardTitle tag='div' className='h5'>
										Şifre
									</CardTitle>
									<CardSubTitle tag='div' className='h6'>
										Şifre değiştirme işlemi
									</CardSubTitle>
								</CardLabel>
								<CardActions>
									{passwordChangeCTA ? (
										<>
											<Button
												color='danger'
												isLight
												icon='Cancel'
												onClick={() => setPasswordChangeCTA(false)}>
												Vazgeç
											</Button>
											<Button
												color='success'
												isLight
												icon='Check'
												onClick={formik.handleSubmit}>
												Değiştir
											</Button>
										</>
									) : (
										<>
											<span>Şifrenizi değiştirmek mi istiyorsunuz?</span>
											<Button
												color='primary'
												isLight
												icon='PublishedWithChanges'
												onClick={() => setPasswordChangeCTA(true)}>
												Evet
											</Button>
										</>
									)}
								</CardActions>
							</CardHeader>
							{passwordChangeCTA && (
								<CardBody>
									<div className='row g-4'>
										<div className='col-12'>
											<FormGroup
												id='password'
												label='Mevcut Şifreniz'
												isFloating>
												<Input
													type='password'
													placeholder='Mevcut Şifreniz'
													autoComplete='current-password'
													onChange={formik.handleChange}
													value={formik.values.password}
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<FormGroup
												id='newPassword'
												label='Yeni Şifre'
												isFloating>
												<Input
													type='password'
													placeholder='Yeni Şifre'
													autoComplete='new-password'
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													value={formik.values.newPassword}
													isValid={formik.isValid}
													isTouched={formik.touched.newPassword}
													invalidFeedback={formik.errors.newPassword}
													validFeedback='Geçerli!'
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<FormGroup
												id='confirmPassword'
												label='Yeni Şifre Tekrar'
												isFloating>
												<Input
													type='password'
													placeholder='Yeni Şifre Tekrar'
													autoComplete='new-password'
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													value={formik.values.confirmPassword}
													isValid={formik.isValid}
													isTouched={formik.touched.confirmPassword}
													invalidFeedback={formik.errors.confirmPassword}
													validFeedback='Geçerli!'
												/>
											</FormGroup>
										</div>
									</div>{' '}
								</CardBody>
							)}
							<CardFooter>
								<CommonDesc>
									Güvenliğiniz için şifrenizi ortalama 3 ayda bir değiştirmenizi tavsiye ederiz.
								</CommonDesc>
							</CardFooter>
						</Card>
					</div>
					<div className='col-md-4'>
						<Card className='position-sticky sticky-top-size'>
							<CardHeader>
								<CardLabel icon='Dashboard'>
									<CardTitle tag='div' className='h5'>
										Hakkımda
									</CardTitle>
									<CardSubTitle>
										Özel öğrenci ya da danışman seçimi yaparken müşterilerin göreceği bilgileri düzenleyiniz.
									</CardSubTitle>
								</CardLabel>
							</CardHeader>
							<CardBody>
								<div className='row g-2'>
									<div className='col-12 row g-2'>
										<div className='col-11'>
											<FormGroup
												id='tag'
												label='Uzmanlık'
												isFloating>
												<Input
													type='text'
													placeholder='Uzmanlık'
													autoComplete='text'
													onChange={e => setTag(e.target.value)}
													value={tag}
												/>
											</FormGroup>
										</div>
										<div className='col-1'>
											<Button color="success" isLight style={{ height: 45 }} icon='Add' onClick={() => {
												if (tag.length < 3) {
													showNotification(
														<span className='d-flex align-items-center'>
															<Icon icon='Info' size='lg' className='me-1' />
															<span>Hata</span>
														</span>,
														"Girdiğiniz uzmanlık bilgisi çok kısa.",
													);
												} else {
													let index = tags.indexOf(tag)
													if (index > -1) {
														showNotification(
															<span className='d-flex align-items-center'>
																<Icon icon='Info' size='lg' className='me-1' />
																<span>Hata</span>
															</span>,
															"Bu uzmanlık daha önce eklenmiş.",
														);
													} else {
														setTags([...tags, tag])
														setTag("")
													}
												}
											}} />
										</div>
									</div>
									<div className='12'>
										{tags.map((t, i) => {
											return <Button icon='Close' className='m-2' onClick={() => {
												setTags([
													...tags.slice(0, i),
													...tags.slice(i + 1)
												])
											}} isLight color='danger'>
												{t}
											</Button>
										})}
									</div>
									<div className='col-12'>
										<FormGroup
											id='instagram'
											label='Instagram URL'
											isFloating>
											<Input
												type='url'
												placeholder='Instagram URL'
												onChange={(e) => setInstagram(e.target.value)}
												value={instagram}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<FormGroup
											id='description'
											label='Hakkında'
											isFloating
											isDisable
											formText='Hakkınızda bilginiz uygulama ya da web sitesinde müşterilerin sizin hakkınızda doğru bilgi alması içindir.'>
											<Textarea
												placeholder='Hakkınızda detaylı bilgi veriniz.'
												autoComplete='description'
												onChange={(e) => setDescription(e.target.value)}
												style={{ minHeight: 100 }}
												value={description}
											/>
										</FormGroup>
									</div>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
				<div className='row'>
					<div className='col-12'>
						<Card>
							<CardBody>
								<div className='row align-items-center'>
									<div className='col'>
										{user.lastUpdated ? (
											<>
												<Icon
													icon='DoneAll'
													size='lg'
													className='me-2 text-muted'
												/>
												<span className='me-2 text-muted'>Son Düzenleme</span>
												<strong>
													{dayjs(user.lastUpdated).format(
														'DD MMMM YYYY - HH:mm',
													)}
												</strong>
											</>
										) : (
											<>
												<Icon
													icon='Warning'
													size='lg'
													className='me-2 text-warning'
												/>
												<span className='text-warning'>Henüz kaydedilmedi.</span>
											</>
										)}
									</div>
									<div className='col-auto'>
										<div className='row g-1'>
											<div className='col-auto'>
												<Button
													className='me-3'
													icon={isLoading ? undefined : 'Save'}
													isLight
													color={lastSave ? 'info' : 'success'}
													isDisable={isLoading}
													onClick={handleSave}>
													{isLoading && <Spinner isSmall inButton />}
													{isLoading
														? (lastSave && 'Saving') || 'Kaydediliyor'
														: (lastSave && 'Save') || 'Kaydet'}
												</Button>
											</div>
											<div className='col-auto'>
												<Dropdown direction='up'>
													<DropdownToggle hasIcon={false}>
														<Button
															color={themeStatus}
															icon='MoreVert'
															aria-label='More'
														/>
													</DropdownToggle>
													<DropdownMenu isAlignmentEnd>
														<DropdownItem>
															<Button
																className='me-3'
																icon='Save'
																isLight
																isDisable={isLoading}
																onClick={formik.resetForm}>
																Sıfırla
															</Button>
														</DropdownItem>
													</DropdownMenu>
												</Dropdown>
											</div>
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default User;
